/**
 * Canonical NGAP catalogue — frontend access.
 *
 * rev v11.0.0 · Phase 1 / B10 + Phase 2
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The catalogue used to be imported as one 981 KB JSON from the root of the
 * bundle. Every page paid for it, including the pages that never show a
 * single act.
 *
 * The catalogue is now split into one chunk per specialty, generated by
 * `pnpm gen:ngap` from the canonical model, and loaded on demand. The
 * frontend never sees a second price model: there is exactly one scale
 * (MAD) and one base amount per act.
 *
 * The French price scale that used to live in `@/lib/referential/ngap`
 * (basePriceCents) is gone. It must not come back: 2500 cents is 25 MAD,
 * while the Moroccan C letter is 80 MAD, so mixing them would under-price
 * a general consultation by 68 %.
 */

/** The pricing status of an act. */
export type PricingStatus = "priced" | "provisional" | "unpriced";

/** One act of the canonical catalogue. */
export interface NgapAct {
  code: string;
  label: string;
  description: string | null;
  category: string;
  coefficient: number;
  /** The secondary coefficient of the nomenclature (anesthesia line). */
  secondaryCoefficient: number;
  /** Base amount in MAD. `null` means the price letter is unknown. */
  baseAmountMad: number | null;
  /** The PRICE key-letter (C, Cs, K, Z, AMI …). */
  priceLetter: string | null;
  /** The ANATOMICAL chapter letter (A = traumatologie, T = radiologie …). */
  chapterLetter: string | null;
  chapterTitle: string | null;
  /** The article of the nomenclature this act belongs to. */
  article: string | null;
  /** The nature of the act, as classified from the nomenclature text. */
  actType: string | null;
  subSpecialty: string | null;
  /** Canonical specialty slugs this act belongs to. */
  applicableSpecialties: string[];
  pricingStatus: PricingStatus;
  pricingConfidence: "high" | "medium" | "low" | "none";
  pricingBasis: string | null;
  /** The source of the extract (the arrêté it comes from). */
  source: string | null;
  sourcePage: number | null;
}

/** A global pricing modifier. */
export interface NgapModifier {
  code: string;
  label: string;
  factor: number;
  scope: string;
  childFactor: number;
  adultFactor: number;
}

import meta from "./ngap/catalogue-meta.json";

/**
 * The global modifiers, in canonical order.
 *
 * Generated from reference-data/ngap/modificateurs.json, which adopts the
 * values the live billing engine applies today (NGAP Maroc arrêté +
 * CNSS circulaire 5-2024). The French table's factors are NOT used.
 */
export const NGAP_MODIFIERS: readonly NgapModifier[] = meta.modifiers as NgapModifier[];

/** The global modifiers, under their historical name. */
export const MODIFICATEURS = NGAP_MODIFIERS;

/** The price key-letters, with their unit value in MAD. */
export const NGAP_LETTRES_CLES = meta.letters;

/** The catalogue hash, so the UI can report which catalogue it shows. */
export const NGAP_CATALOG_HASH = meta.hash;

/** The statistics of the catalogue. */
export const NGAP_CATALOG_STATS = meta.stats;

/**
 * The per-specialty chunks.
 *
 * `import.meta.glob` is what makes this work: Vite sees the literal pattern
 * and emits one JS chunk per JSON file, each loaded only when it is first
 * imported. A plain `import()` with a variable would not be split.
 */
const CHUNKS = import.meta.glob("./ngap/*/acts.json", { eager: false }) as Record<
  string,
  () => Promise<{ default: NgapAct[] }>
>;

const chunkFor = (slug: string) => CHUNKS[`./ngap/${slug}/acts.json`];

/** The slugs that have a catalogue chunk. */
export const NGAP_SPECIALTY_SLUGS: readonly string[] = Object.keys(CHUNKS)
  .map((p) => p.replace(/^\.\/ngap\//, "").replace(/\/acts\.json$/, ""))
  .sort();

const cache = new Map<string, Promise<NgapAct[]>>();

/**
 * Load the acts of one specialty.
 *
 * The chunk is fetched once and cached for the lifetime of the tab. An
 * unknown specialty resolves to an empty list rather than throwing: the UI
 * shows an empty state, it does not crash.
 */
export function loadSpecialtyActs(slug: string): Promise<NgapAct[]> {
  const key = slug.trim().toLowerCase();
  const cached = cache.get(key);
  if (cached) return cached;
  const loader = chunkFor(key);
  if (!loader) {
    const empty = Promise.resolve<NgapAct[]>([]);
    cache.set(key, empty);
    return empty;
  }
  const promise = loader().then((mod) => mod.default ?? []);
  cache.set(key, promise);
  return promise;
}

/**
 * Load every act of the catalogue.
 *
 * Used by the referential page, which legitimately shows the whole
 * nomenclature. It loads the 15 chunks in parallel — still lazy with
 * respect to the rest of the application.
 */
export async function loadAllActs(): Promise<NgapAct[]> {
  const lists = await Promise.all(NGAP_SPECIALTY_SLUGS.map((s) => loadSpecialtyActs(s)));
  const all = lists.flat();
  all.sort((a, b) => a.code.localeCompare(b.code, "fr"));
  return all;
}

/**
 * The sub-specialties of a specialty, with their act counts.
 * Computed from the loaded chunk, so it is async by nature.
 */
export function subSpecialtiesOf(acts: readonly NgapAct[]): Array<{ id: string; count: number }> {
  const counts = new Map<string, number>();
  for (const act of acts) {
    const key = act.subSpecialty || "Général";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([id, count]) => ({ id, count }))
    .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));
}

/**
 * The base amount of an act in dirhams, with the modifiers applied.
 *
 * Returns `null` when the act has no price — never 0, which would mean
 * "free" and would silently under-bill the claim.
 */
export function calculateActPrice(
  act: NgapAct,
  modifierCodes: readonly string[] = [],
): number | null {
  if (act.baseAmountMad === null) return null;
  let price = act.baseAmountMad;
  for (const code of modifierCodes) {
    const modifier = NGAP_MODIFIERS.find((m) => m.code === code);
    if (!modifier) continue;
    if (modifier.scope !== "all" && act.category !== modifier.scope) continue;
    price *= modifier.factor;
  }
  return Math.round(price * 100) / 100;
}

/**
 * Formats an amount for display.
 *
 * A null amount is UNKNOWN, not free: the extract does not publish a
 * coefficient for 126 of the 2561 acts, and rendering those as "0.00 MAD"
 * would tell a prescriber that the act costs nothing. The dash says what
 * is true — that the amount has not been established.
 */
export function formatAmountMad(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "—";
  return `${amount.toFixed(2)} MAD`;
}

/** The French label of each canonical category. */
export const CATEGORY_LABEL: Record<string, string> = {
  consultation: "Consultations",
  visite: "Visites",
  "acte-technique": "Actes techniques",
  biologie: "Biologie",
  imagerie: "Imagerie",
  orthodontie: "Dentaire",
  "sage-femme": "Sage-femme",
  kyotherapie: "Kinésithérapie",
  "soins-infirmiers": "Soins infirmiers",
  transport: "Transport",
  autre: "Autres",
};

/** The badge tone of each canonical category. */
export const CATEGORY_TONE: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  consultation: "default",
  visite: "secondary",
  "acte-technique": "outline",
  biologie: "secondary",
  imagerie: "outline",
  orthodontie: "secondary",
  "sage-femme": "outline",
  kyotherapie: "secondary",
  "soins-infirmiers": "secondary",
  transport: "outline",
  autre: "outline",
};

/** The rules of the nomenclature, shown in the referential page. */
export const NGAP_RULES: readonly string[] = [
  "Un acte technique ne peut être coté qu'en association avec une consultation ou une visite, sauf exception prévue par la nomenclature",
  "Le coefficient d'un acte est multiplié par la valeur de la lettre-clé correspondante",
  "Le montant de base est exprimé en dirhams (MAD) : base = coefficient × valeur de la lettre-clé",
  "Les modificateurs (F dimanche, S soir, N nuit, M maintien à domicile, U urgence, P personne âgée) s'appliquent au montant de base",
  "Les actes dont la lettre-clé de valorisation n'est pas établie portent un prix provisoire : ils ne doivent pas être adressés au payeur comme définitifs",
];
